import { Directive, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';

@Directive({
    selector: '[ridangoHideFromScreenreader]'
})
export class HideFromScreenreaderDirective implements OnInit, OnDestroy {
    private observer: MutationObserver;
    private hostObserver?: MutationObserver;
    private controlsWrapper?: HTMLElement;

    constructor(private el: ElementRef<HTMLElement>) {}

    ngOnInit(): void {
        // To ensure consistency across platforms and different iOS versions:
        // Android TalkBack and iOS VoiceOver have two regions which are focusable the map container, and buttons
        // Mimics nested leaflet like structure to ensure CSS classes behave same as it were in the actual map container
        if (Capacitor.isNativePlatform()) {
            const observer = new MutationObserver(() => {
                const el = this.el.nativeElement;
                const controlContainer = el.querySelector('.leaflet-control-container');
                if (controlContainer && el.parentElement) {
                    const classes = [
                        'space-safe-zone map-controls-offset',
                        'rounded-controls map-wrapper icon-size-large',
                        'map !h-full leaflet-container leaflet-touch leaflet-retina leaflet-fade-anim leaflet-grab leaflet-touch-drag leaflet-touch-zoom'
                    ].reduceRight((child, classes) => {
                        const div = document.createElement('div');
                        div.className = classes;
                        if (child) div.appendChild(child);

                        const locateObserver = new MutationObserver(() => {
                            div.querySelectorAll('.leaflet-control-locate[aria-hidden]').forEach((el) => {
                                el.removeAttribute('aria-hidden');
                                locateObserver.disconnect();
                            });
                        });

                        locateObserver.observe(div, {
                            childList: true,
                            subtree: true,
                            attributes: true,
                            attributeFilter: ['aria-hidden']
                        });

                        return div;
                    }, controlContainer);

                    this.controlsWrapper = classes as HTMLElement;
                    el.parentElement.insertBefore(this.controlsWrapper, el);
                    this.syncControlsWrapperFromHost();

                    this.hostObserver = new MutationObserver(() => this.syncControlsWrapperFromHost());
                    this.hostObserver.observe(el, {
                        attributes: true,
                        attributeFilter: ['class', 'style']
                    });
                    // We need to observe the map container because leaflet adds them at runtime
                    // Once done we can disconnect the observer
                    observer.disconnect();
                }
            });

            observer.observe(this.el.nativeElement, { childList: true, subtree: true });
        }

        // When dynamic content is added to the map, we need to set aria-hidden on it (busses, icons)
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const el = node as HTMLElement;

                            if (
                                el.closest('.leaflet-map-pane') ||
                                el.classList.contains('hidden') ||
                                el.classList.contains('leaflet-control-attribution')
                            ) {
                                this.applyAriaHiddenRecursive(el);
                            }
                        }
                    });
                } else if (mutation.type === 'attributes') {
                    const target = mutation.target as HTMLElement;

                    if (
                        target.classList.contains('hidden') ||
                        target.classList.contains('leaflet-control-attribution')
                    ) {
                        target.setAttribute('aria-hidden', 'true');
                        target.setAttribute('tabindex', '-1');
                    }

                    if (target.closest('.leaflet-map-pane')) {
                        if (!target.classList.contains('leaflet-container')) {
                            target.setAttribute('aria-hidden', 'true');
                        }

                        this.applyTabIndexRecursive(target);
                    }
                }
            });
        });

        this.observer.observe(this.el.nativeElement, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class']
        });
    }

    private applyAriaHiddenRecursive(element: HTMLElement): void {
        element.setAttribute('aria-hidden', 'true');
        this.applyTabIndexRecursive(element);
        Array.from(element.children).forEach((child) => {
            this.applyAriaHiddenRecursive(child as HTMLElement);
        });
    }

    private applyTabIndexRecursive(element: HTMLElement): void {
        const focusableSelector =
            'a[href], button, input, textarea, select, [tabindex], [contenteditable="true"], [role="button"], [role="link"]';

        if (element.matches(focusableSelector)) {
            element.setAttribute('tabindex', '-1');
        }

        Array.from(element.querySelectorAll(focusableSelector)).forEach((childElement) => {
            childElement.setAttribute('tabindex', '-1');
        });
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.hostObserver) {
            this.hostObserver.disconnect();
        }
    }

    private syncControlsWrapperFromHost(): void {
        const host = this.el?.nativeElement;
        if (!host || !this.controlsWrapper) {
            return;
        }

        const hasSpaceSafeZone = host.classList.contains('space-safe-zone');
        const hasMapControlsOffset = host.classList.contains('map-controls-offset');
        const useDefaultSafeZone = !hasSpaceSafeZone && !hasMapControlsOffset;

        this.controlsWrapper.classList.toggle('space-safe-zone', hasSpaceSafeZone || useDefaultSafeZone);
        this.controlsWrapper.classList.toggle('map-controls-offset', hasMapControlsOffset);

        const offset = host.style.getPropertyValue('--leaflet-view-settings-offset');
        if (offset) {
            this.controlsWrapper.style.setProperty('--leaflet-view-settings-offset', offset);
        } else {
            this.controlsWrapper.style.removeProperty('--leaflet-view-settings-offset');
        }
    }
}
